import React, { useEffect, useMemo, useState } from "react";
import { Skeleton, Stack } from "@mui/material";
import { HiLanguage, HiSquares2X2, HiStar } from "react-icons/hi2";
import MainButton from "../../components/common/MainButtonComponent";
import ExamFlowConfirmModal from "./ExamFlowConfirmModal";
import { useExamContext } from "../../context/Exam/ExamContext";
import Filter from "./Filter";
import AssessmentPagination from "./AssessmentPagination";
import "./AssessShared.css";
import "./ExamSection.css";
import { AssessmentService } from "../../service/candidate/assessmentService";

const examFilterConfig = [
  { key: "grade", label: "Grade", icon: HiStar, optionKey: "gradeOptions" },
  { key: "language", label: "Language", icon: HiLanguage, optionKey: "languageOptions" },
  { key: "subject", label: "Subject", icon: HiSquares2X2, optionKey: "subjectOptions" },
  { key: "examCategory", label: "Exam Category", icon: HiSquares2X2, optionKey: "examCategoryOptions" },
];

const toSelectOptions = (list, fieldConfig = {}) => {
  const idKeys = fieldConfig.idKeys || [];
  const labelKeys = fieldConfig.labelKeys || [];

  const values = (list || [])
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") {
        const value = String(item);
        return { value, label: value };
      }

      if (!item || typeof item !== "object") return null;

      const idKey = idKeys.find(
        (candidateKey) => item[candidateKey] !== undefined && item[candidateKey] !== null
      );

      const labelKey = labelKeys.find(
        (candidateKey) => item[candidateKey] !== undefined && item[candidateKey] !== null
      );

      if (!idKey || !labelKey) return null;

      return {
        value: String(item[idKey]),
        label: String(item[labelKey]),
      };
    })
    .filter(Boolean);

  return [...new Map(values.map((option) => [option.value, option])).values()];
};

const toArrayFromQueryValue = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (value === null || value === undefined || value === "null" || value === "") return [];

  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const toQueryValue = (value) => {
  const values = toArrayFromQueryValue(value);
  return values.length ? values.join(",") : "null";
};

const getMyExamAction = (item) => {
  if (item.candidate_status === "Finished") return "grade-report";
  if (item.candidate_status === "Onprogress") return "resume";
  return "start";
};

const mapMyExamItem = (item) => ({
  ...item,
  name: item.assess_name || item.title || item.name || "Untitled Exam",
  time: item.time || (item.exam_time_interval ? `${item.exam_time_interval} minutes` : "Unlimited"),
  attempt: `${item.attempt_no || 1} out of ${item.no_of_attempts || 1}`,
  action: getMyExamAction(item),
});

export default function ExamList({ items, onGradeReport, onExamConsent, viewMode = "new-exams" }) {
  const {
    query,
    totalCount,
    updateQuery,
    metaData,
    metaLoading,
    loading,
    tryAgainExam,
  } = useExamContext();

  const [pendingExam, setPendingExam] = useState(null);
  const [retryingExamId, setRetryingExamId] = useState(null);
  const [latestAttemptMap, setLatestAttemptMap] = useState({});
  const myExamItems = useMemo(
    () => (items || []).map(mapMyExamItem),
    [items]
  );

  useEffect(() => {
    let isMounted = true;

    const loadLatestAttempts = async () => {
      const loginUserId = sessionStorage.getItem("user_id");

      const assessmentIds = [
        ...new Set(
          myExamItems
            .map((item) => item.assessment_id || item.id)
            .filter(Boolean)
            .map(String)
        ),
      ];

      if (!loginUserId || assessmentIds.length === 0) {
        if (isMounted) {
          setLatestAttemptMap({});
        }
        return;
      }

      const results = await Promise.all(
        assessmentIds.map(async (assessmentId) => {
          try {
            const response = await AssessmentService.getAllLastAttemp(
              loginUserId,
              assessmentId
            );

            return {
              assessmentId,
              latestAttempt: response?.data || null,
            };
          } catch (error) {
            console.error(
              `Failed to load latest attempt for assessment ${assessmentId}`,
              error
            );

            return {
              assessmentId,
              latestAttempt: null,
            };
          }
        })
      );

      if (!isMounted) return;

      const nextMap = {};

      results.forEach(({ assessmentId, latestAttempt }) => {
        nextMap[String(assessmentId)] = latestAttempt;
      });

      setLatestAttemptMap(nextMap);
    };

    loadLatestAttempts();

    return () => {
      isMounted = false;
    };
  }, [myExamItems]);

  const openExamConfirm = (item, mode) => {
    setPendingExam({ item, mode });
  };

  const confirmExamRedirect = () => {
    if (pendingExam) {
      onExamConsent(pendingExam.item, pendingExam.mode);
    }
    setPendingExam(null);
  };

  // const hasLaterAttempt = (currentItem) => {
  //   return myExamItems.some((otherItem) => {
  //     return (
  //       String(otherItem.assessment_id) === String(currentItem.assessment_id) &&
  //       Number(otherItem.attempt_no || 1) > Number(currentItem.attempt_no || 1)
  //     );
  //   });
  // };

  const canShowTryAgain = (item) => {
    if (viewMode !== "grade-reports") {
      return false;
    }

    if (item.candidate_status !== "Finished") {
      return false;
    }

    const assessmentId = String(item.assessment_id || item.id);

    const latestAttempt = latestAttemptMap[assessmentId];

    // Do not show the button until the latest attempt is confirmed.
    if (!latestAttempt) {
      return false;
    }

    const displayedAttemptNo = Number(item.attempt_no || 1);
    const latestAttemptNo = Number(latestAttempt.attempt_no || 1);
    const allowedAttempts = Number(
      latestAttempt.no_of_attempts || item.no_of_attempts || 1
    );

    // Only the latest attempt can display Try Again.
    if (displayedAttemptNo !== latestAttemptNo) {
      return false;
    }

    // The latest attempt must already be completed.
    if (latestAttempt.candidate_status !== "Finished") {
      return false;
    }

    // Hide when all allowed attempts are completed.
    if (latestAttemptNo >= allowedAttempts) {
      return false;
    }

    return true;
  };

  const examFilterOptions = useMemo(() => ({
    gradeOptions: toSelectOptions(metaData.Grade, {
      idKeys: ["grade_id"],
      labelKeys: ["grade"],
    }),
    languageOptions: toSelectOptions(metaData.Language, {
      idKeys: ["language_id"],
      labelKeys: ["language"],
    }),
    subjectOptions: toSelectOptions(metaData.Subject, {
      idKeys: ["subject_id"],
      labelKeys: ["subject"],
    }),
    examCategoryOptions: toSelectOptions(metaData.Exam_Category, {
      idKeys: ["exam_category_id"],
      labelKeys: ["name"],
    }),
  }), [metaData]);

  const examApiFilters = useMemo(() => ({
    search_term: query.search_term || "",
    sort_column: query.sort_column || "created_at",
    sort_direction: query.sort_direction || "desc",
    selectedGrades: toArrayFromQueryValue(query.selectedGrades),
    selectedLanguages: toArrayFromQueryValue(query.selectedLanguages),
    selectedsubjects: toArrayFromQueryValue(query.selectedsubjects),
    selectedExamCategories: toArrayFromQueryValue(query.selectedExamCategories),
    selectedStartDate: query.selectedStartDate || "",
    selectedEndDate: query.selectedEndDate || "",
  }), [query]);

  const handleExamFilterChange = async (filterQueryFromCommonFilter) => {
    updateQuery({
      search_term: filterQueryFromCommonFilter.search_term || "",
      sort_column: filterQueryFromCommonFilter.sort_column || query.sort_column,
      sort_direction:
        filterQueryFromCommonFilter.sort_direction || query.sort_direction,
      selectedGrades: toQueryValue(filterQueryFromCommonFilter.selectedGrades),
      selectedLanguages: toQueryValue(filterQueryFromCommonFilter.selectedLanguages),
      selectedsubjects: toQueryValue(filterQueryFromCommonFilter.selectedsubjects),
      selectedExamCategories: toQueryValue(filterQueryFromCommonFilter.selectedExamCategories),
      selectedStartDate: filterQueryFromCommonFilter.selectedStartDate || "null",
      selectedEndDate: filterQueryFromCommonFilter.selectedEndDate || "null",
      offset: 0,
    });
  };

  return (
    <div className="exam-view">
      <Filter
        metaDataOptions={examFilterOptions}
        onQueryChange={handleExamFilterChange}
        apiFilters={examApiFilters}
        syncKey="my-exams"
        count={totalCount}
        countLabel="Number of Exams"
        searchAriaLabel="Search exams"
        panelFirst={true}
        filterConfigOverride={examFilterConfig}
      />

      <div className="exam-list">
        {myExamItems.length === 0 ? (
          metaLoading || loading ? (
            <div style={{ padding: 24 }}>
              <Stack spacing={2}>
                <Skeleton variant="rectangular" width="100%" height={56} />
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} variant="rectangular" width="100%" height={64} />
                ))}
              </Stack>
            </div>
          ) : (
              <div className="exam-empty-state">
                <h3>
                  {viewMode === "grade-reports" ? "No grade reports found" : "No new exams found"}
                </h3>
                <h3>No exams found</h3>
                <p>Adjust filters or search to find exams.</p>
              </div>
            )
        ) : (
          myExamItems.map((item) => (
            <article
              className="exam-row row g-2 align-items-center"
              key={`${item.assessment_id}-${item.candidate_id || "new"}-${item.attempt_no}`}
            >
              <div className="col-12 col-md">
                <h3>{item.name}</h3>
                <div className="exam-row__meta">
                  <span>Time: {item.time}</span>
                  <span>Attempt: {item.attempt}</span>
                  {/* <span>Status: {item.status}</span> */}
                </div>
              </div>

              <div className="col-12 col-md-auto">
                {item.action === "grade-report" ? (
                  <div className="exam-row__actions">
                    <MainButton
                      action="view"
                      icon="view"
                      label="Grade Report"
                      tone="success"
                      className="exam-action-btn exam-action-btn--report"
                      onClick={() => onGradeReport(item)}
                    />

                    {canShowTryAgain(item) && (
                      // <MainButton
                      //   action="submit"
                      //   icon="submit"
                      //   label="Try Again"
                      //   className="exam-action-btn exam-action-btn--start"
                      //   onClick={() => tryAgainExam(item)}
                      // />

                      <MainButton
                        action="submit"
                        icon="submit"
                        label={
                          retryingExamId === item.candidate_id
                            ? "Adding Attempt..."
                            : "Try Again"
                        }
                        className="exam-action-btn exam-action-btn--start"
                        disabled={retryingExamId === item.candidate_id}
                        onClick={async () => {
                          setRetryingExamId(item.candidate_id);

                          try {
                            await tryAgainExam(item);
                          } finally {
                            setRetryingExamId(null);
                          }
                        }}
                      />
                    )}

                  </div>
                ) : (

                  <MainButton
                    action="submit"
                    icon="submit"
                    label={item.action === "resume" ? "Resume Exam" : "Start Exam"}
                    className={`exam-action-btn ${item.action === "resume"
                      ? "exam-action-btn--resume"
                      : "exam-action-btn--start"
                      }`}
                    onClick={() =>
                      openExamConfirm(
                        item,
                        item.action === "resume" ? "resume" : "start"
                      )
                    }
                  />
                )}
              </div>
            </article>
          ))
        )}
      </div>

      <AssessmentPagination
        page={Math.floor(Number(query.offset || 0) / Number(query.limit || 10)) + 1}
        pageSize={Number(query.limit || 10)}
        totalCount={totalCount}
        onPageChange={(page) =>
          updateQuery({
            offset: (page - 1) * Number(query.limit || 10),
          })
        }
        onPageSizeChange={(pageSize) =>
          updateQuery({
            limit: Number(pageSize),
            offset: 0,
          })
        }
      />

      <ExamFlowConfirmModal
        open={Boolean(pendingExam)}
        title="Start exam mode?"
        message="This will open the consent step and disable the sidebar until you leave the exam area."
        onClose={() => setPendingExam(null)}
        onConfirm={confirmExamRedirect}
      />
    </div>
  );
}
